import { useState, useCallback, useEffect } from 'react';
import { apiService } from '../../../../services/api';
import { Windpark, PlanOptionsResponse } from '../../../../types';
import { hasFieldChanged } from '../../../../utils/formUtils';

interface UseFrameworkDataProps {
  project: Windpark;
  onProjectUpdated?: () => void;
}

interface UseFrameworkDataReturn {
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fieldErrors: Record<string, string>;
  planOptions: PlanOptionsResponse | null;
  updateField: (field: string, value: string | number | boolean | null) => Promise<void>;
}

/**
 * Formular-Feld-Validierung vor dem API-Call (siehe ADR 003).
 * Pure Funktion: Prüft den neuen Wert gegen den aktuellen Projekt-Kontext.
 * Gibt eine Fehlermeldung zurück oder undefined wenn alles ok ist.
 */
function validateField(
  field: string,
  value: string | number | boolean | null,
  project: Windpark,
): string | undefined {
  if (field === 'inbetriebnahme' && value && project.baubeginn && value <= project.baubeginn) {
    return 'Inbetriebnahme muss nach dem Baubeginn liegen';
  }
  if (field === 'baubeginn' && value && project.inbetriebnahme && value >= project.inbetriebnahme) {
    return 'Baubeginn muss vor der Inbetriebnahme liegen';
  }
  return undefined;
}

/**
 * Hook für Stammdaten und Risikokennzahlen des Projektrahmens.
 * Nutzt den bestehenden PATCH-Endpoint für partielle Updates.
 * Lädt beim Mount die verfügbaren Plan-Optionen vom Backend.
 */
export const useFrameworkData = ({ project, onProjectUpdated }: UseFrameworkDataProps): UseFrameworkDataReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [planOptions, setPlanOptions] = useState<PlanOptionsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiService.getPlanOptions().then((options) => {
      if (!cancelled) setPlanOptions(options);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const updateField = useCallback(async (field: string, value: string | number | boolean | null) => {
    const fieldError = validateField(field, value, project);
    if (fieldError) {
      setFieldErrors(prev => ({ ...prev, [field]: fieldError }));
      return;
    }
    setFieldErrors(prev => {
      const { [field]: _, ...rest } = prev;
      return rest;
    });

    if (!hasFieldChanged(project, field, value)) return;

    try {
      setIsSaving(true);
      setError(null);
      await apiService.updateProjectPartial(project.id, { [field]: value });
      onProjectUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  }, [project, onProjectUpdated]);

  return {
    isLoading,
    isSaving,
    error,
    fieldErrors,
    planOptions,
    updateField,
  };
};
